import { unwrap, type Schemas } from '@mrigl/api-client';
import type { GovernorateCode, Locale } from '@mrigl/shared';
import { Alert, Button, CheckboxGroup, SelectField, TextAreaField, TextField } from '@mrigl/ui';
import { useEffect, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n } from '../../i18n.js';
import {
  cleanText,
  display,
  storeLocales,
  toMajor,
  toMinor,
  useStore,
  type Localized,
  type StoreDetails,
} from '../catalog/shared.js';
import { checkoutError, fromBasisPoints, governorateOptions, toBasisPoints } from './shared.js';

type Settings = Schemas['CheckoutSettings'];
type State = Schemas['CheckoutSettingsState'];

interface ZoneDraft {
  readonly key: string;
  readonly name: Localized;
  readonly governorates: readonly GovernorateCode[];
  readonly price: string;
  readonly freeOver: string;
}

interface ClassDraft {
  readonly key: string;
  readonly name: Localized;
  readonly rate: string;
}

interface Draft {
  readonly cashOnDelivery: boolean;
  readonly bankTransfer: boolean;
  readonly bankInstructions: Localized;
  readonly shipping: boolean;
  readonly zones: readonly ZoneDraft[];
  readonly pickup: boolean;
  readonly pickupAddress: Localized;
  readonly taxes: boolean;
  readonly pricesIncludeTax: boolean;
  readonly fiscalId: string;
  readonly classes: readonly ClassDraft[];
  readonly defaultClass: string;
  readonly shippingClass: string;
  readonly fee: string;
  readonly feeLabel: Localized;
}

function fromSettings(settings: Settings): Draft {
  return {
    cashOnDelivery: settings.payments.cashOnDelivery.enabled,
    bankTransfer: settings.payments.bankTransfer.enabled,
    bankInstructions: settings.payments.bankTransfer.instructions,
    shipping: settings.shipping.enabled,
    zones: settings.shipping.zones.map((zone) => ({
      key: zone.key,
      name: zone.name,
      governorates: zone.governorates,
      price: toMajor(zone.price.minor),
      freeOver: zone.freeOver === null ? '' : toMajor(zone.freeOver.minor),
    })),
    pickup: settings.pickup.enabled,
    pickupAddress: settings.pickup.address,
    taxes: settings.taxes.enabled,
    pricesIncludeTax: settings.taxes.pricesIncludeTax,
    fiscalId: settings.taxes.fiscalId ?? '',
    classes: settings.taxes.classes.map((item) => ({
      key: item.key,
      name: item.name,
      rate: fromBasisPoints(item.rateBp),
    })),
    defaultClass: settings.taxes.defaultClass ?? '',
    shippingClass: settings.taxes.shippingClass ?? '',
    fee: settings.fee.amount === null ? '' : toMajor(settings.fee.amount.minor),
    feeLabel: settings.fee.label,
  };
}

const KEY = /^[a-z0-9-]{1,40}$/;
const tnd = (minor: string) => ({ minor, currency: 'TND' as const });

/** Store checkout: payment methods, delivery zones and pickup, taxes, fixed fee. */
export function CheckoutSettingsPage() {
  const { storeId = '' } = useParams();
  const { t } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [state, setState] = useState<State | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/checkout-settings', {
        params: { path: { storeId } },
      }),
    )
      .then(setState)
      .catch((failure: unknown) => {
        setLoadError(errorMessage(t, failure));
      });
  }, [storeId, t]);

  const error = storeError ?? loadError;
  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (store === null || state === null) return <Loading />;
  return <Editor store={store} initial={state} />;
}

function Editor({ store, initial }: { readonly store: StoreDetails; readonly initial: State }) {
  const { t, locale } = useI18n();
  const locales = storeLocales(store);
  const canManage = store.permissions.includes('checkout.manage');
  const [version, setVersion] = useState(initial.version);
  const [saved, setSaved] = useState<Settings>(initial.settings);
  const [draft, setDraft] = useState<Draft>(() => fromSettings(initial.settings));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const governorates = governorateOptions(locale);
  const languageName = (code: Locale) => t(`admin.language.${code}`);
  const update = (change: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...change }));
  };
  const updateZone = (index: number, change: Partial<ZoneDraft>) => {
    update({
      zones: draft.zones.map((zone, position) =>
        position === index ? { ...zone, ...change } : zone,
      ),
    });
  };
  const updateClass = (index: number, change: Partial<ClassDraft>) => {
    update({
      classes: draft.classes.map((item, position) =>
        position === index ? { ...item, ...change } : item,
      ),
    });
  };

  function build(): Settings | null {
    const found: Record<string, string> = {};
    const amount = (text: string, path: string, optional: boolean) => {
      if (optional && text.trim() === '') return null;
      const minor = toMinor(text);
      if (minor === null) found[path] = t('admin.checkout.invalidAmount');
      return minor;
    };
    const zones = draft.zones.map((zone, index) => {
      const price = amount(zone.price, `zone-${String(index)}-price`, false);
      const freeOver = amount(zone.freeOver, `zone-${String(index)}-freeOver`, true);
      return {
        key: zone.key,
        name: cleanText(zone.name),
        governorates: [...zone.governorates],
        price: tnd(price ?? '0'),
        freeOver: freeOver === null ? null : tnd(freeOver),
      };
    });
    const classes = draft.classes.map((item, index) => {
      const rateBp = toBasisPoints(item.rate);
      if (rateBp === null) found[`class-${String(index)}-rate`] = t('admin.checkout.invalidRate');
      if (!KEY.test(item.key.trim())) {
        found[`class-${String(index)}-key`] = t('admin.checkout.invalidKey');
      }
      return { key: item.key.trim(), name: cleanText(item.name), rateBp: rateBp ?? 0 };
    });
    const fee = amount(draft.fee, 'fee', true);
    setErrors(found);
    if (Object.keys(found).length > 0) return null;
    const known = new Set(classes.map((item) => item.key));
    const classOrNull = (key: string) => (key !== '' && known.has(key) ? key : null);
    return {
      schemaVersion: 1,
      taxes: {
        enabled: draft.taxes,
        pricesIncludeTax: draft.pricesIncludeTax,
        fiscalId: draft.fiscalId.trim() === '' ? null : draft.fiscalId.trim(),
        classes,
        defaultClass: classOrNull(draft.defaultClass),
        shippingClass: classOrNull(draft.shippingClass),
      },
      fee: {
        amount: fee === null || fee === '0' ? null : tnd(fee),
        label: cleanText(draft.feeLabel),
      },
      payments: {
        cashOnDelivery: { enabled: draft.cashOnDelivery },
        bankTransfer: {
          enabled: draft.bankTransfer,
          instructions: cleanText(draft.bankInstructions),
        },
      },
      shipping: { enabled: draft.shipping, zones },
      pickup: { enabled: draft.pickup, address: cleanText(draft.pickupAddress) },
    };
  }

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(null);
    setError(null);
    const settings = build();
    if (settings === null) {
      setError(t('admin.checkout.error.validation'));
      return;
    }
    setBusy(true);
    try {
      const result = await unwrap(
        api.PUT('/admin-api/v1/stores/{storeId}/checkout-settings', {
          params: { path: { storeId: store.storeId } },
          body: { settings, version: Math.max(version, 1) },
        }),
      );
      setVersion(result.version);
      setSaved(result.settings);
      setDraft(fromSettings(result.settings));
      setNotice(t('admin.checkout.saved'));
    } catch (failure) {
      setError(checkoutError(t, failure, 'checkout'));
    } finally {
      setBusy(false);
    }
  }

  const available =
    (saved.payments.cashOnDelivery.enabled || saved.payments.bankTransfer.enabled) &&
    ((saved.shipping.enabled && saved.shipping.zones.length > 0) || saved.pickup.enabled);
  const classOptions = [
    { value: '', label: t('admin.checkout.noClass') },
    ...draft.classes
      .filter((item) => KEY.test(item.key.trim()))
      .map((item) => ({
        value: item.key.trim(),
        label: `${display(item.name, locale, store, item.key)} (${item.rate} %)`,
      })),
  ];
  const takenElsewhere = (index: number) =>
    new Set(draft.zones.flatMap((zone, position) => (position === index ? [] : zone.governorates)));

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${store.storeId}`}>
          {display(store.name, locale, store, store.slug)}
        </Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.checkout.title')}</h1>
      <p className="mrigl-card">
        <strong>
          {available ? t('admin.checkout.available') : t('admin.checkout.unavailable')}
        </strong>
      </p>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <form
        className="mrigl-stack"
        aria-label={t('admin.checkout.title')}
        onSubmit={(event) => void save(event)}
      >
        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.checkout.payments')}</legend>
          <CheckboxGroup
            legend={t('admin.checkout.payments')}
            options={[
              { value: 'cod', label: t('admin.checkout.cashOnDelivery') },
              { value: 'bank', label: t('admin.checkout.bankTransfer') },
            ]}
            values={[
              ...(draft.cashOnDelivery ? ['cod'] : []),
              ...(draft.bankTransfer ? ['bank'] : []),
            ]}
            onChange={(values) => {
              update({
                cashOnDelivery: values.includes('cod'),
                bankTransfer: values.includes('bank'),
              });
            }}
          />
          {draft.bankTransfer
            ? locales.map((code) => (
                <TextAreaField
                  key={`bank-${code}`}
                  lang={code}
                  dir="auto"
                  label={t('admin.checkout.bankInstructionsIn', { language: languageName(code) })}
                  hint={t('admin.checkout.bankInstructionsHint')}
                  maxLength={1000}
                  required={code === store.defaultLocale}
                  value={draft.bankInstructions[code] ?? ''}
                  onChange={(event) => {
                    update({
                      bankInstructions: { ...draft.bankInstructions, [code]: event.target.value },
                    });
                  }}
                />
              ))
            : null}
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.checkout.delivery')}</legend>
          <CheckboxGroup
            legend={t('admin.checkout.delivery')}
            options={[
              { value: 'shipping', label: t('admin.checkout.shippingEnabled') },
              { value: 'pickup', label: t('admin.checkout.pickupEnabled') },
            ]}
            values={[...(draft.shipping ? ['shipping'] : []), ...(draft.pickup ? ['pickup'] : [])]}
            onChange={(values) => {
              update({ shipping: values.includes('shipping'), pickup: values.includes('pickup') });
            }}
          />
          {draft.shipping ? (
            <>
              {draft.zones.map((zone, index) => {
                const number = String(index + 1);
                const taken = takenElsewhere(index);
                return (
                  <fieldset key={zone.key} className="mrigl-fieldset mrigl-stack">
                    <legend className="mrigl-field__label">
                      {t('admin.checkout.zone', { number })}
                    </legend>
                    {locales.map((code) => (
                      <TextField
                        key={`zone-${zone.key}-${code}`}
                        lang={code}
                        dir="auto"
                        label={t('admin.checkout.zoneNameIn', { language: languageName(code) })}
                        maxLength={60}
                        value={zone.name[code] ?? ''}
                        onChange={(event) => {
                          updateZone(index, { name: { ...zone.name, [code]: event.target.value } });
                        }}
                      />
                    ))}
                    <TextField
                      label={t('admin.checkout.zonePrice')}
                      inputMode="decimal"
                      required
                      value={zone.price}
                      error={errors[`zone-${String(index)}-price`]}
                      onChange={(event) => {
                        updateZone(index, { price: event.target.value });
                      }}
                    />
                    <TextField
                      label={t('admin.checkout.zoneFreeOver')}
                      inputMode="decimal"
                      value={zone.freeOver}
                      error={errors[`zone-${String(index)}-freeOver`]}
                      onChange={(event) => {
                        updateZone(index, { freeOver: event.target.value });
                      }}
                    />
                    <CheckboxGroup
                      legend={t('admin.checkout.zoneGovernorates')}
                      options={governorates.filter((option) => !taken.has(option.value))}
                      values={zone.governorates}
                      onChange={(values) => {
                        updateZone(index, { governorates: values as GovernorateCode[] });
                      }}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        update({ zones: draft.zones.filter((_, position) => position !== index) });
                      }}
                    >
                      {t('admin.checkout.removeZone', { number })}
                    </Button>
                  </fieldset>
                );
              })}
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  update({
                    zones: [
                      ...draft.zones,
                      {
                        key: `zone-${Math.random().toString(36).slice(2, 8)}`,
                        name: {},
                        governorates: [],
                        price: '',
                        freeOver: '',
                      },
                    ],
                  });
                }}
              >
                {t('admin.checkout.addZone')}
              </Button>
            </>
          ) : null}
          {draft.pickup
            ? locales.map((code) => (
                <TextAreaField
                  key={`pickup-${code}`}
                  lang={code}
                  dir="auto"
                  label={t('admin.checkout.pickupAddressIn', { language: languageName(code) })}
                  maxLength={300}
                  required={code === store.defaultLocale}
                  value={draft.pickupAddress[code] ?? ''}
                  onChange={(event) => {
                    update({
                      pickupAddress: { ...draft.pickupAddress, [code]: event.target.value },
                    });
                  }}
                />
              ))
            : null}
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.checkout.taxes')}</legend>
          <p className="mrigl-muted">{t('admin.checkout.taxesHint')}</p>
          <CheckboxGroup
            legend={t('admin.checkout.taxes')}
            options={[{ value: 'taxes', label: t('admin.checkout.taxesEnabled') }]}
            values={draft.taxes ? ['taxes'] : []}
            onChange={(values) => {
              update({ taxes: values.includes('taxes') });
            }}
          />
          {draft.taxes ? (
            <>
              <SelectField
                label={t('admin.checkout.pricesIncludeTax')}
                value={draft.pricesIncludeTax ? 'yes' : 'no'}
                onChange={(event) => {
                  update({ pricesIncludeTax: event.target.value === 'yes' });
                }}
                options={[
                  { value: 'yes', label: t('admin.checkout.pricesIncludeTax.yes') },
                  { value: 'no', label: t('admin.checkout.pricesIncludeTax.no') },
                ]}
              />
              <TextField
                label={t('admin.checkout.fiscalId')}
                maxLength={40}
                dir="ltr"
                value={draft.fiscalId}
                onChange={(event) => {
                  update({ fiscalId: event.target.value });
                }}
              />
              {draft.classes.map((item, index) => {
                const number = String(index + 1);
                return (
                  <fieldset key={`class-${number}`} className="mrigl-fieldset mrigl-stack">
                    <legend className="mrigl-field__label">
                      {t('admin.checkout.taxClass', { number })}
                    </legend>
                    <TextField
                      label={t('admin.checkout.taxClassKey')}
                      dir="ltr"
                      maxLength={40}
                      required
                      value={item.key}
                      error={errors[`class-${String(index)}-key`]}
                      onChange={(event) => {
                        updateClass(index, { key: event.target.value.toLowerCase() });
                      }}
                    />
                    {locales.map((code) => (
                      <TextField
                        key={`class-${number}-${code}`}
                        lang={code}
                        dir="auto"
                        label={t('admin.checkout.taxClassNameIn', { language: languageName(code) })}
                        maxLength={60}
                        value={item.name[code] ?? ''}
                        onChange={(event) => {
                          updateClass(index, {
                            name: { ...item.name, [code]: event.target.value },
                          });
                        }}
                      />
                    ))}
                    <TextField
                      label={t('admin.checkout.taxClassRate')}
                      inputMode="decimal"
                      required
                      value={item.rate}
                      error={errors[`class-${String(index)}-rate`]}
                      onChange={(event) => {
                        updateClass(index, { rate: event.target.value });
                      }}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        update({
                          classes: draft.classes.filter((_, position) => position !== index),
                        });
                      }}
                    >
                      {t('admin.checkout.removeTaxClass', { number })}
                    </Button>
                  </fieldset>
                );
              })}
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  update({ classes: [...draft.classes, { key: '', name: {}, rate: '' }] });
                }}
              >
                {t('admin.checkout.addTaxClass')}
              </Button>
              <SelectField
                label={t('admin.checkout.defaultClass')}
                value={draft.defaultClass}
                onChange={(event) => {
                  update({ defaultClass: event.target.value });
                }}
                options={classOptions}
              />
              <SelectField
                label={t('admin.checkout.shippingClass')}
                value={draft.shippingClass}
                onChange={(event) => {
                  update({ shippingClass: event.target.value });
                }}
                options={classOptions}
              />
            </>
          ) : null}
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.checkout.fee')}</legend>
          <p className="mrigl-muted">{t('admin.checkout.feeHint')}</p>
          <TextField
            label={t('admin.checkout.feeAmount')}
            inputMode="decimal"
            value={draft.fee}
            error={errors['fee']}
            onChange={(event) => {
              update({ fee: event.target.value });
            }}
          />
          {draft.fee.trim() === ''
            ? null
            : locales.map((code) => (
                <TextField
                  key={`fee-${code}`}
                  lang={code}
                  dir="auto"
                  label={t('admin.checkout.feeLabelIn', { language: languageName(code) })}
                  maxLength={60}
                  required={code === store.defaultLocale}
                  value={draft.feeLabel[code] ?? ''}
                  onChange={(event) => {
                    update({ feeLabel: { ...draft.feeLabel, [code]: event.target.value } });
                  }}
                />
              ))}
        </fieldset>

        {canManage ? (
          <Button type="submit" busy={busy}>
            {t('admin.save')}
          </Button>
        ) : null}
      </form>
    </div>
  );
}
